import type { VenueDetail } from '../types';

interface VenueDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  venue: VenueDetail | null;
  onEdit: () => void;
  onToggleStatus: () => void;
}

export default function VenueDetailModal({ isOpen, onClose, venue, onEdit, onToggleStatus }: VenueDetailModalProps) {
  if (!isOpen || !venue) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">场所详情</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-base font-semibold text-gray-900 mb-4">基本信息</h4>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-gray-500 mb-1">ID</div>
                <div className="text-sm text-gray-900">{venue.id}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">名称</div>
                <div className="text-sm text-gray-900">{venue.name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">租户</div>
                <div className="text-sm text-gray-900">{venue.tenantName}</div>
              </div>
              <div className="col-span-3">
                <div className="text-sm text-gray-500 mb-1">地址</div>
                <div className="text-sm text-gray-900">
                  {venue.address.province} {venue.address.city} {venue.address.district} {venue.address.detail}
                </div>
              </div>
              {venue.description && (
                <div className="col-span-3">
                  <div className="text-sm text-gray-500 mb-1">描述</div>
                  <div className="text-sm text-gray-900">{venue.description}</div>
                </div>
              )}
              <div>
                <div className="text-sm text-gray-500 mb-1">状态</div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  venue.status === 'enabled' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {venue.status === 'enabled' ? '启用' : '停用'}
                </span>
              </div>
              {venue.enabledTime && (
                <div>
                  <div className="text-sm text-gray-500 mb-1">启用时间</div>
                  <div className="text-sm text-gray-900">{venue.enabledTime}</div>
                </div>
              )}
              <div>
                <div className="text-sm text-gray-500 mb-1">面积</div>
                <div className="text-sm text-gray-900">{venue.area} 平米</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">创建时间</div>
                <div className="text-sm text-gray-900">{venue.createdAt}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">创建人</div>
                <div className="text-sm text-gray-900">{venue.createdBy}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">更新时间</div>
                <div className="text-sm text-gray-900">{venue.updatedAt}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">更新人</div>
                <div className="text-sm text-gray-900">{venue.updatedBy}</div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-base font-semibold text-gray-900 mb-4">
              瘦终端列表 ({venue.thinClients.length})
            </h4>
            {venue.thinClients.length > 0 ? (
              <div className="bg-white rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">编号</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">名称</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">序列号</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">状态</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">MAC</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">固件版本</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">最近开机时间</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {venue.thinClients.map((client, index) => (
                      <tr key={client.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{client.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{client.serialNumber}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            client.status === 'running' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {client.status === 'running' ? '运行中' : '空闲'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{client.mac}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{client.firmwareVersion}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{client.lastBootTime}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">暂无瘦终端数据</div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              onClick={onEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              编辑
            </button>
            <button
              onClick={onToggleStatus}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                venue.status === 'enabled'
                  ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  : 'text-white bg-green-600 hover:bg-green-700'
              }`}
            >
              {venue.status === 'enabled' ? '停用' : '启用'}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
